import type { Recipe } from "@/pages/sy/models/Recipe";
import type { ModalData } from "../models/recipe-types";
import { MenuOptionType } from "@dexteel/mesf-core";
import {
  Delete as DeleteIcon,
  PlaylistAdd as PlaylistAddIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Upgrade as UpgradeIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
} from "@mui/icons-material";

interface Props {
  setRecipe: (recipe: Recipe) => void;
  setDeleteModalData: (data: ModalData) => void;
  handleNewRecipe: () => void;
  setUpgradeModalData: (data: ModalData) => void;
  setCloneModalData?: (data: ModalData) => void;
  onSetAsCurrent?: (recipe: Recipe) => void;
  onToggleEnable?: (recipe: Recipe) => void;
}

export const useRecipeOptionsFunctions = ({
  setRecipe,
  setDeleteModalData,
  handleNewRecipe,
  setUpgradeModalData,
  setCloneModalData,
  onSetAsCurrent,
  onToggleEnable,
}: Props) => {
  const getMenuOptions = (data: Recipe) => {
    const options: MenuOptionType[] = [
      {
        name: "New Recipe",
        key: "new_recipe",
        onClick: () => {
          handleNewRecipe();
        },
        icon: <PlaylistAddIcon />,
      },
    ];

    if (data) {
      // New Version (upgrade within same family)
      options.push({
        name: "New Version",
        key: "upgrade_recipe",
        onClick: () => {
          setRecipe(data);
          setUpgradeModalData({ open: true, recipe: data });
        },
        icon: <UpgradeIcon />,
      });

      // Clone (create new independent recipe family)
      if (setCloneModalData) {
        options.push({
          name: "Clone",
          key: "clone_recipe",
          onClick: () => {
            setRecipe(data);
            setCloneModalData({ open: true, recipe: data });
          },
          icon: <ContentCopyIcon />,
        });
      }

      // Set as Current (only for non-current versions)
      if (onSetAsCurrent && !data.IsCurrent) {
        options.push({
          name: "Set as Current",
          key: "set_current",
          onClick: () => {
            onSetAsCurrent(data);
          },
          icon: <CheckCircleOutlineIcon />,
        });
      }

      // Enable/Disable (only for current versions - parent rows)
      if (onToggleEnable && data.IsCurrent) {
        options.push({
          name: data.IsEnable ? "Disable Recipe" : "Enable Recipe",
          key: "toggle_enable",
          onClick: () => {
            onToggleEnable(data);
          },
          icon: data.IsEnable ? <ToggleOffIcon /> : <ToggleOnIcon />,
        });
      }

      // Delete
      options.push({
        name: "Delete Recipe",
        key: "delete_recipe",
        onClick: () => {
          setRecipe(data);
          setDeleteModalData({ open: true, recipe: data });
        },
        icon: <DeleteIcon />,
      });
    }
    return options;
  };

  return {
    getMenuOptions,
  };
};
